// Turns the API's clip versions and messages into what the clip workspace shows. A version's
// `review` comes in three kinds:
//   review    the scored card: score, headline, what changed, fix next (format "review")
//   fallback  a scored-format card where the model's answer couldn't be used: measurements only
//   legacy    analyses from before clips existed: { overall_strategy, recommendations[] }, no score

/** A short word for a score, used on collapsed older versions */
export const scoreVerdict = (score) => {
  if (score == null) return null;
  if (score >= 9) return 'In great shape';
  if (score >= 7) return 'Getting there';
  if (score >= 5) return 'Needs work';
  return 'Needs a lot of work';
};

const text = (value) => (typeof value === 'string' ? value.trim() : '');

// Legacy settings were an object of { parameter: value }
const legacyMoves = (recommendations) => (Array.isArray(recommendations) ? recommendations : [])
  .filter((rec) => rec && typeof rec === 'object' && text(rec.stage))
  .map((rec) => ({
    plugin: text(rec.stage),
    target: text(rec.priority),
    purpose: text(rec.rationale),
    settings: rec.technical_settings && typeof rec.technical_settings === 'object'
      ? Object.entries(rec.technical_settings).map(([label, value]) => [label, String(value)])
      : [],
  }));

/**
 * Everything the analysis card shows for one version, in the design system's prop names.
 */
export const analysisCardModel = (version) => {
  const review = version?.review || {};

  if (version?.format !== 'review') {
    return {
      kind: 'legacy',
      score: null,
      headline: 'Your earlier analysis',
      summary: text(review.overall_strategy),
      moves: legacyMoves(review.recommendations),
    };
  }

  return {
    kind: review.fallback ? 'fallback' : 'review',
    score: typeof version.score === 'number' ? version.score : null,
    scoreKind: review.score_kind,
    previousScore: typeof review.previous_score === 'number' ? review.previous_score : null,
    previousVersion: review.previous_version ?? null,
    headline: text(review.headline),
    summary: text(review.summary),
    changes: (review.changes || []).map((change) => ({
      label: change.label,
      verdict: change.verdict,
      needsAttention: Boolean(change.needs_attention),
      explanation: change.explanation,
    })),
    everythingElse: text(review.everything_else),
    nextFix: review.next_fix && text(review.next_fix.title) ? review.next_fix : null,
    moves: Array.isArray(review.moves) ? review.moves : [],
  };
};

/** An older version collapsed to one line: score, a verdict, its main issue and a recap */
export const versionCardProps = (version) => {
  const card = analysisCardModel(version);
  const firstLegacyMove = card.kind === 'legacy' ? card.moves[0]?.plugin : null;
  const issue = card.nextFix?.title || firstLegacyMove;
  return {
    version: version.version,
    filename: version.filename,
    score: card.score,
    verdict: card.kind === 'legacy' ? 'Earlier analysis' : scoreVerdict(card.score),
    mainIssue: issue ? `Fix next: ${issue}` : card.kind === 'review' ? 'Nothing urgent' : null,
    recap: card.summary,
  };
};

const timestamp = (item) => Date.parse(item.created_at || '') || 0;

/**
 * The clip's chat in order: each version (the latest marked) and the messages around it, by time.
 * When a version and a message share a time, the version comes first.
 */
export const buildThread = (versions = [], messages = []) => {
  const latest = versions.reduce((max, v) => Math.max(max, v.version || 0), 0);
  const items = [
    ...versions.map((version) => ({ type: 'version', key: `v${version.id}`, version, latest: version.version === latest })),
    ...messages.map((message) => ({ type: 'message', key: `m${message.id}`, message })),
  ];
  return items.sort((a, b) => {
    const at = timestamp(a.version || a.message);
    const bt = timestamp(b.version || b.message);
    if (at !== bt) return at - bt;
    if (a.type !== b.type) return a.type === 'version' ? -1 : 1;
    return (a.version || a.message).id - (b.version || b.message).id;
  });
};
